import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import PublicLayout from "../components/PublicLayout";
import PublicJobCard from "../components/PublicJobCard";

const PublicCompany = () => {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const companyResponse = await axiosInstance.get(`/get-user/${slug}`);
        const companyData = companyResponse.data?.Mpage || null;
        setCompany(companyData);

        if (companyData?._id) {
          const jobsResponse = await axiosInstance.get(
            `/job-portal/jobs/public?limit=20&recruiterId=${companyData._id}`
          );
          setJobs(jobsResponse.data?.data?.jobs || []);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  return (
    <PublicLayout>
      <Helmet>
        <title>{company?.username ? `${company.username} | Hiring Partner` : "Employer Profile"}</title>
      </Helmet>

      <section className="portal-hero border-bottom text-start">
        <div className="portal-container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : !company ? (
            <div className="text-center py-5">
              <h2 className="h5 fw-bold portal-muted">Organization not found.</h2>
            </div>
          ) : (
            <div className="py-3 d-flex align-items-start gap-3 flex-wrap">
              <div className="portal-brand-mark" style={{ width: 64, height: 64, fontSize: "1.35rem" }}>
                {(company.username || "C").slice(0, 1).toUpperCase()}
              </div>
              <div>
                <span className="portal-kicker">Employer Showcase</span>
                <h1 className="portal-title mb-2">{company.username}</h1>
                <div className="d-flex align-items-center gap-2 portal-muted small flex-wrap">
                  <span className="fw-bold text-dark">{company.Designation || "Hiring Partner"}</span>
                  <span>|</span>
                  <span>{company.statename || company.address || "Location verified"}</span>
                  {company.website ? (
                    <>
                      <span>|</span>
                      <a href={company.website} className="text-decoration-none">Website</a>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {!loading && company ? (
        <section className="portal-section">
          <div className="portal-container">
            <div className="row gy-4">
              <div className="col-lg-8">
                <div className="mb-3">
                  <span className="portal-kicker">Openings</span>
                  <h2 className="h4 fw-bold">Active jobs at {company?.username}</h2>
                </div>

                {jobs.length === 0 ? (
                  <div className="p-4 border rounded text-center bg-light">
                    <p className="portal-muted mb-0">There are currently no active job openings from this employer.</p>
                  </div>
                ) : (
                  <div className="portal-job-grid">
                    {jobs.map((job) => (
                      <PublicJobCard job={job} key={job.id} />
                    ))}
                  </div>
                )}
              </div>

              <aside className="col-lg-4">
                <div className="d-grid gap-3">
                  <div className="portal-card p-3">
                    <h3 className="h6 fw-bold mb-2">About organization</h3>
                    <p className="portal-muted small mb-0">
                      {company?.about || "This organization has not yet provided a detailed professional summary."}
                    </p>
                  </div>

                  <div className="portal-card p-3">
                    <h3 className="h6 fw-bold mb-2">Contact info</h3>
                    <div className="d-grid gap-1 small portal-muted">
                      <div>{company?.email || "Email hidden"}</div>
                      <div>{company?.phone || "Contact hidden"}</div>
                      <div>{company?.address || "Location verified"}</div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      ) : null}
    </PublicLayout>
  );
};

export default PublicCompany;
